/**
 * 治愈系加载状态组件
 * 使用手绘风格小图标 + 温馨文案
 */

import React from 'react';
import './LoadingWarm.css';

interface LoadingWarmProps {
  type?: 'cloud' | 'tree' | 'sun' | 'cat';
  tip?: string;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

const LoadingWarm: React.FC<LoadingWarmProps> = ({
  type = 'cloud',
  tip,
  size = 'default',
  style,
}) => {
  const tips = tip || getDefaultTip();
  const sizeClass = `warm-loading-${size}`;

  return (
    <div className={`warm-loading-container ${sizeClass}`} style={style}>
      <div className="warm-loading-icon-wrapper">
        {getIcon(type)}
      </div>
      <div className="warm-loading-tip">{tips}</div>
    </div>
  );
};

export default LoadingWarm;

// 获取默认文案
const getDefaultTip = (): string => {
  const tips = [
    '数据正在路上，请稍候~',
    '小树苗正在努力生长...',
    '云朵在收集数据中~',
    '猫咪正在搬运数据...',
    '阳光洒满数据花园~',
  ];
  return tips[Math.floor(Math.random() * tips.length)];
};

// 获取图标
const getIcon = (type: string) => {
  switch (type) {
    case 'tree':
      return <TreeIcon />;
    case 'sun':
      return <SunIcon />;
    case 'cat':
      return <CatIcon />;
    case 'cloud':
    default:
      return <CloudIcon />;
  }
};

// ========== 图标组件 ==========

const CloudIcon: React.FC = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" className="warm-loading-svg">
    <path
      d="M30 15 C20 15 13 22 13 30 C13 36 18 42 26 42 C34 42 40 36 40 30 C40 22 35 15 30 15 Z"
      fill="#FFF5E6"
      stroke="#D4C0A8"
      strokeWidth="2"
    >
      <animate
        attributeName="opacity"
        values="0.3;1;0.3"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </path>
    <circle cx="24" cy="26" r="3" fill="#F8D7DA">
      <animate
        attributeName="r"
        values="2;3;2"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </circle>
    <circle cx="36" cy="26" r="3" fill="#D4EDDA">
      <animate
        attributeName="r"
        values="2;3;2"
        dur="1.5s"
        repeatCount="indefinite"
        begin="0.5s"
      />
    </circle>
  </svg>
);

const TreeIcon: React.FC = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" className="warm-loading-svg">
    {/* 树干 */}
    <rect x="26" y="35" width="8" height="15" rx="4" fill="#8B7355">
      <animate
        attributeName="height"
        values="15;18;15"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </rect>

    {/* 树叶 */}
    <circle cx="30" cy="25" r="12" fill="#D4EDDA">
      <animate
        attributeName="r"
        values="10;12;10"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </circle>
    <circle cx="25" cy="22" r="8" fill="#8FBC8F">
      <animate
        attributeName="r"
        values="7;8;7"
        dur="1.5s"
        repeatCount="indefinite"
        begin="0.5s"
      />
    </circle>
    <circle cx="35" cy="22" r="8" fill="#8FBC8F">
      <animate
        attributeName="r"
        values="7;8;7"
        dur="1.5s"
        repeatCount="indefinite"
        begin="1s"
      />
    </circle>
  </svg>
);

const SunIcon: React.FC = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" className="warm-loading-svg">
    <circle cx="30" cy="30" r="10" fill="#FFF3CD">
      <animate
        attributeName="r"
        values="8;10;8"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </circle>

    {/* 光芒 */}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, index) => (
      <line
        key={index}
        x1="30"
        y1="30"
        x2={30 + Math.cos((angle * Math.PI) / 180) * 20}
        y2={30 + Math.sin((angle * Math.PI) / 180) * 20}
        stroke="#FFF3CD"
        strokeWidth="2"
      >
        <animate
          attributeName="opacity"
          values="0.3;1;0.3"
          dur="1.5s"
          repeatCount="indefinite"
          begin={`${index * 0.1}s`}
        />
      </line>
    ))}
  </svg>
);

const CatIcon: React.FC = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" className="warm-loading-svg">
    <circle cx="30" cy="30" r="15" fill="#F8D7DA">
      <animate
        attributeName="r"
        values="13;15;13"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </circle>

    {/* 耳朵 */}
    <circle cx="24" cy="24" r="4" fill="#F8D7DA" />
    <circle cx="36" cy="24" r="4" fill="#F8D7DA" />

    {/* 眼睛 */}
    <circle cx="26" cy="28" r="2" fill="#5C4033">
      <animate
        attributeName="r"
        values="1.5;2;1.5"
        dur="1.5s"
        repeatCount="indefinite"
        begin="0.5s"
      />
    </circle>
    <circle cx="34" cy="28" r="2" fill="#5C4033">
      <animate
        attributeName="r"
        values="1.5;2;1.5"
        dur="1.5s"
        repeatCount="indefinite"
        begin="1s"
      />
    </circle>

    {/* 胡须 */}
    <line x1="20" y1="32" x2="26" y2="32" stroke="#5C4033" strokeWidth="1">
      <animate
        attributeName="x1"
        values="18;20;18"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </line>
    <line x1="40" y1="32" x2="34" y2="32" stroke="#5C4033" strokeWidth="1">
      <animate
        attributeName="x1"
        values="42;40;42"
        dur="1.5s"
        repeatCount="indefinite"
        begin="0.5s"
      />
    </line>
  </svg>
);
